import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentProposals.css";

const EMPTY_PROPOSAL_FORM = {
  client_id: "",
  project_id: "",
  proposal_number: "",
  title: "",
  introduction: "",
  scope_summary: "",
  terms: "",
  estimated_timeline: "",
  deposit_percentage: "30",
  discount_amount: "0",
  tax_amount: "0",
  expiration_date: "",
  status: "draft",
  sent: false,
  sent_date: "",
  accepted: false,
  accepted_date: "",
  notes: "",
};

const EMPTY_ITEM_FORM = {
  service_id: "",
  title: "",
  description: "",
  quantity: "1",
  unit_price: "0",
  optional: false,
  approved: true,
  display_order: "0",
};

const STATUS_OPTIONS = [
  "draft",
  "sent",
  "accepted",
  "declined",
  "expired",
  "cancelled",
];

function safeString(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}

function cleanText(value) {
  return safeString(value).trim();
}

function optionalText(value) {
  const cleaned = cleanText(value);

  return cleaned || null;
}

function normalizeDateInput(value) {
  return value
    ? safeString(value).slice(0, 10)
    : "";
}

function formatCurrency(value) {
  const amount = Number(value);

  return new Intl.NumberFormat(
    "en-US",
    {
      style: "currency",
      currency: "USD",
    }
  ).format(
    Number.isFinite(amount)
      ? amount
      : 0
  );
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(
    `${safeString(value).slice(
      0,
      10
    )}T00:00:00`
  );

  if (
    Number.isNaN(date.getTime())
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function formatLabel(value) {
  return safeString(value)
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );
}

function getClientName(client) {
  if (!client) {
    return "Unknown client";
  }

  const personalName = [
    client.first_name,
    client.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    client.business_name ||
    personalName ||
    `Client ${client.id}`
  );
}

function normalizeProposal(
  proposal = {}
) {
  return {
    client_id:
      proposal.client_id == null
        ? ""
        : String(
            proposal.client_id
          ),

    project_id:
      proposal.project_id == null
        ? ""
        : String(
            proposal.project_id
          ),

    proposal_number:
      safeString(
        proposal.proposal_number
      ),

    title:
      safeString(
        proposal.title
      ),

    introduction:
      safeString(
        proposal.introduction
      ),

    scope_summary:
      safeString(
        proposal.scope_summary
      ),

    terms:
      safeString(
        proposal.terms
      ),

    estimated_timeline:
      safeString(
        proposal.estimated_timeline
      ),

    deposit_percentage:
      proposal.deposit_percentage ==
      null
        ? "30"
        : String(
            proposal.deposit_percentage
          ),

    discount_amount:
      proposal.discount_amount == null
        ? "0"
        : String(
            proposal.discount_amount
          ),

    tax_amount:
      proposal.tax_amount == null
        ? "0"
        : String(
            proposal.tax_amount
          ),

    expiration_date:
      normalizeDateInput(
        proposal.expiration_date
      ),

    status:
      safeString(
        proposal.status
      ) || "draft",

    sent:
      Number(proposal.sent) === 1,

    sent_date:
      normalizeDateInput(
        proposal.sent_date
      ),

    accepted:
      Number(
        proposal.accepted
      ) === 1,

    accepted_date:
      normalizeDateInput(
        proposal.accepted_date
      ),

    notes:
      safeString(
        proposal.notes
      ),
  };
}

function normalizeItem(item = {}) {
  return {
    service_id:
      item.service_id == null
        ? ""
        : String(
            item.service_id
          ),

    title:
      safeString(item.title),

    description:
      safeString(
        item.description
      ),

    quantity:
      item.quantity == null
        ? "1"
        : String(item.quantity),

    unit_price:
      item.unit_price == null
        ? "0"
        : String(
            item.unit_price
          ),

    optional:
      Number(item.optional) === 1,

    approved:
      Number(item.approved) === 1,

    display_order:
      item.display_order == null
        ? "0"
        : String(
            item.display_order
          ),
  };
}

function DevelopmentProposals() {
  const [proposals, setProposals] =
    useState([]);

  const [clients, setClients] =
    useState([]);

  const [projects, setProjects] =
    useState([]);

  const [services, setServices] =
    useState([]);

  const [
    selectedProposal,
    setSelectedProposal,
  ] = useState(null);

  const [
    selectedItem,
    setSelectedItem,
  ] = useState(null);

  const [
    proposalItems,
    setProposalItems,
  ] = useState([]);

  const [
    proposalForm,
    setProposalForm,
  ] = useState({
    ...EMPTY_PROPOSAL_FORM,
  });

  const [itemForm, setItemForm] =
    useState({
      ...EMPTY_ITEM_FORM,
    });

  const [
    showProposalForm,
    setShowProposalForm,
  ] = useState(false);

  const [
    showItemManager,
    setShowItemManager,
  ] = useState(false);

  const [
    showItemForm,
    setShowItemForm,
  ] = useState(false);

  const [search, setSearch] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");

  const [
    clientFilter,
    setClientFilter,
  ] = useState("");

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [
    isLoadingItems,
    setIsLoadingItems,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const loadData = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const [
          proposalResponse,
          clientResponse,
          projectResponse,
          serviceResponse,
        ] = await Promise.all([
          Api.getDevelopmentProposals(),
          Api.getDevelopmentClients(),
          Api.getDevelopmentProjects(),
          Api.getDevelopmentServices(),
        ]);

        setProposals(
          proposalResponse?.proposals ||
            []
        );

        setClients(
          clientResponse?.clients ||
            []
        );

        setProjects(
          projectResponse?.projects ||
            []
        );

        setServices(
          serviceResponse?.services ||
            []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Proposals could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clientMap = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          Number(client.id),
          client,
        ])
      ),
    [clients]
  );

  const projectMap = useMemo(
    () =>
      new Map(
        projects.map((project) => [
          Number(project.id),
          project,
        ])
      ),
    [projects]
  );

  const availableProjects =
    useMemo(() => {
      if (!proposalForm.client_id) {
        return projects;
      }

      return projects.filter(
        (project) =>
          String(
            project.client_id
          ) ===
          String(
            proposalForm.client_id
          )
      );
    }, [
      projects,
      proposalForm.client_id,
    ]);

  const filteredProposals =
    useMemo(() => {
      const normalizedSearch =
        search
          .trim()
          .toLowerCase();

      return proposals.filter(
        (proposal) => {
          if (
            statusFilter !== "all" &&
            String(
              proposal.status
            ) !== statusFilter
          ) {
            return false;
          }

          if (
            clientFilter &&
            String(
              proposal.client_id
            ) !==
              String(clientFilter)
          ) {
            return false;
          }

          if (!normalizedSearch) {
            return true;
          }

          const client =
            clientMap.get(
              Number(
                proposal.client_id
              )
            );

          const project =
            projectMap.get(
              Number(
                proposal.project_id
              )
            );

          const searchableText = [
            proposal.proposal_number,
            proposal.title,
            proposal.status,
            proposal.scope_summary,
            proposal.introduction,
            proposal.estimated_timeline,
            getClientName(client),
            project?.title,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchableText.includes(
            normalizedSearch
          );
        }
      );
    }, [
      proposals,
      search,
      statusFilter,
      clientFilter,
      clientMap,
      projectMap,
    ]);

  const summary = useMemo(
    () => ({
      total:
        proposals.length,

      draft:
        proposals.filter(
          (proposal) =>
            proposal.status ===
            "draft"
        ).length,

      sent:
        proposals.filter(
          (proposal) =>
            Number(
              proposal.sent
            ) === 1 &&
            Number(
              proposal.accepted
            ) !== 1
        ).length,

      accepted:
        proposals.filter(
          (proposal) =>
            Number(
              proposal.accepted
            ) === 1
        ).length,

      totalValue:
        proposals.reduce(
          (total, proposal) =>
            total +
            (
              Number(
                proposal.total_amount
              ) || 0
            ),
          0
        ),
    }),
    [proposals]
  );

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openNewProposalForm =
    () => {
      clearMessages();

      setSelectedProposal(null);

      setProposalForm({
        ...EMPTY_PROPOSAL_FORM,
      });

      setShowProposalForm(true);
    };

  const openEditProposalForm = (
    proposal
  ) => {
    clearMessages();

    setSelectedProposal(
      proposal
    );

    setProposalForm(
      normalizeProposal(
        proposal
      )
    );

    setShowProposalForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeProposalForm =
    () => {
      setSelectedProposal(null);

      setProposalForm({
        ...EMPTY_PROPOSAL_FORM,
      });

      setShowProposalForm(false);
    };

  const handleProposalChange = (
    event
  ) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    setProposalForm(
      (current) => {
        const next = {
          ...current,

          [name]:
            type === "checkbox"
              ? checked
              : value,
        };

        if (
          name === "client_id"
        ) {
          next.project_id = "";
        }

        if (
          name === "sent" &&
          !checked
        ) {
          next.sent_date = "";
          next.accepted = false;
          next.accepted_date = "";

          if (
            next.status ===
              "sent" ||
            next.status ===
              "accepted"
          ) {
            next.status = "draft";
          }
        }

        if (
          name === "sent" &&
          checked
        ) {
          if (
            next.status === "draft"
          ) {
            next.status = "sent";
          }
        }

        if (
          name === "accepted" &&
          checked
        ) {
          next.sent = true;
          next.status = "accepted";
        }

        if (
          name === "accepted" &&
          !checked
        ) {
          next.accepted_date = "";

          if (
            next.status ===
            "accepted"
          ) {
            next.status =
              next.sent
                ? "sent"
                : "draft";
          }
        }

        if (name === "status") {
          if (value === "draft") {
            next.sent = false;
            next.sent_date = "";
            next.accepted = false;
            next.accepted_date =
              "";
          }

          if (value === "sent") {
            next.sent = true;
            next.accepted = false;
            next.accepted_date =
              "";
          }

          if (
            value === "accepted"
          ) {
            next.sent = true;
            next.accepted = true;
          }

          if (
            [
              "declined",
              "expired",
              "cancelled",
            ].includes(value)
          ) {
            next.accepted = false;
            next.accepted_date =
              "";
          }
        }

        return next;
      }
    );
  };

  const validateProposalForm =
    () => {
      if (
        !proposalForm.client_id
      ) {
        return "A client is required.";
      }

      if (
        !cleanText(
          proposalForm.title
        )
      ) {
        return "Proposal title is required.";
      }

      const depositPercentage =
        Number(
          proposalForm.deposit_percentage
        );

      if (
        !Number.isFinite(
          depositPercentage
        ) ||
        depositPercentage < 0 ||
        depositPercentage > 100
      ) {
        return "Deposit percentage must be between 0 and 100.";
      }

      const discount =
        Number(
          proposalForm.discount_amount ||
            0
        );

      if (
        !Number.isFinite(
          discount
        ) ||
        discount < 0
      ) {
        return "Discount amount must be 0 or greater.";
      }

      const tax =
        Number(
          proposalForm.tax_amount ||
            0
        );

      if (
        !Number.isFinite(tax) ||
        tax < 0
      ) {
        return "Tax amount must be 0 or greater.";
      }

      return "";
    };

  const buildProposalPayload =
    () => ({
      client_id:
        Number(
          proposalForm.client_id
        ),

      project_id:
        proposalForm.project_id
          ? Number(
              proposalForm.project_id
            )
          : null,

      proposal_number:
        cleanText(
          proposalForm.proposal_number
        ) || undefined,

      title:
        cleanText(
          proposalForm.title
        ),

      introduction:
        optionalText(
          proposalForm.introduction
        ),

      scope_summary:
        optionalText(
          proposalForm.scope_summary
        ),

      terms:
        optionalText(
          proposalForm.terms
        ),

      estimated_timeline:
        optionalText(
          proposalForm.estimated_timeline
        ),

      deposit_percentage:
        Number(
          proposalForm.deposit_percentage ||
            0
        ),

      discount_amount:
        Number(
          proposalForm.discount_amount ||
            0
        ),

      tax_amount:
        Number(
          proposalForm.tax_amount ||
            0
        ),

      expiration_date:
        normalizeDateInput(
          proposalForm.expiration_date
        ) || null,

      status:
        proposalForm.status,

      sent:
        Boolean(
          proposalForm.sent
        ),

      sent_date:
        proposalForm.sent
          ? normalizeDateInput(
              proposalForm.sent_date
            ) || null
          : null,

      accepted:
        Boolean(
          proposalForm.accepted
        ),

      accepted_date:
        proposalForm.accepted
          ? normalizeDateInput(
              proposalForm.accepted_date
            ) || null
          : null,

      notes:
        optionalText(
          proposalForm.notes
        ),
    });

  const handleProposalSubmit =
    async (event) => {
      event.preventDefault();
      clearMessages();

      const validationError =
        validateProposalForm();

      if (validationError) {
        setError(
          validationError
        );

        return;
      }

      setIsSaving(true);

      try {
        const payload =
          buildProposalPayload();

        if (selectedProposal) {
          await Api.updateDevelopmentProposal(
            selectedProposal.id,
            payload
          );

          setSuccess(
            "Proposal updated successfully."
          );
        } else {
          await Api.createDevelopmentProposal(
            payload
          );

          setSuccess(
            "Proposal created successfully."
          );
        }

        await loadData();

        closeProposalForm();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The proposal could not be saved."
        );
      } finally {
        setIsSaving(false);
      }
    };

  const updateProposal =
    async (
      proposal,
      changes,
      message
    ) => {
      clearMessages();

      try {
        await Api.updateDevelopmentProposal(
          proposal.id,
          changes
        );

        setSuccess(message);

        await loadData();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The proposal could not be updated."
        );
      }
    };

  const markProposalSent = (
    proposal
  ) => {
    updateProposal(
      proposal,
      {
        sent: true,
        status: "sent",
      },
      "Proposal marked as sent."
    );
  };

  const markProposalAccepted = (
    proposal
  ) => {
    updateProposal(
      proposal,
      {
        sent: true,
        accepted: true,
        status: "accepted",
      },
      "Proposal marked as accepted."
    );
  };

  const deactivateProposal =
    async (proposal) => {
      const confirmed =
        window.confirm(
          `Cancel proposal "${proposal.title}"?`
        );

      if (!confirmed) {
        return;
      }

      await updateProposal(
        proposal,
        {
          status: "cancelled",
          accepted: false,
        },
        "Proposal cancelled."
      );
    };

  const openItemManager =
    async (proposal) => {
      clearMessages();

      setSelectedProposal(
        proposal
      );

      setProposalItems([]);

      setShowItemManager(
        true
      );

      setShowItemForm(false);

      setSelectedItem(null);

      setItemForm({
        ...EMPTY_ITEM_FORM,
      });

      setIsLoadingItems(true);

      try {
        const response =
          await Api.getDevelopmentProposalItems(
            proposal.id
          );

        setProposalItems(
          response?.items || []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Proposal items could not be loaded."
        );
      } finally {
        setIsLoadingItems(
          false
        );
      }
    };

  const closeItemManager =
    () => {
      setShowItemManager(
        false
      );

      setShowItemForm(false);

      setSelectedItem(null);

      setProposalItems([]);

      setItemForm({
        ...EMPTY_ITEM_FORM,
      });

      setSelectedProposal(null);
    };

  const openNewItemForm = () => {
    setSelectedItem(null);

    setItemForm({
      ...EMPTY_ITEM_FORM,
    });

    setShowItemForm(true);
  };

  const openEditItemForm = (
    item
  ) => {
    setSelectedItem(item);

    setItemForm(
      normalizeItem(item)
    );

    setShowItemForm(true);
  };

  const handleItemChange = (
    event
  ) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    setItemForm((current) => {
      const next = {
        ...current,

        [name]:
          type === "checkbox"
            ? checked
            : value,
      };

      if (
        name === "service_id" &&
        value
      ) {
        const service =
          services.find(
            (entry) =>
              String(entry.id) ===
              String(value)
          );

        if (service) {
          next.title =
            current.title ||
            service.name ||
            "";

          next.description =
            current.description ||
            service.public_description ||
            "";

          next.unit_price =
            service.default_price ==
            null
              ? current.unit_price
              : String(
                  service.default_price
                );
        }
      }

      return next;
    });
  };

  const validateItemForm =
    () => {
      if (
        !cleanText(
          itemForm.title
        )
      ) {
        return "Proposal item title is required.";
      }

      const quantity =
        Number(
          itemForm.quantity
        );

      if (
        !Number.isFinite(
          quantity
        ) ||
        quantity <= 0
      ) {
        return "Quantity must be greater than 0.";
      }

      const unitPrice =
        Number(
          itemForm.unit_price
        );

      if (
        !Number.isFinite(
          unitPrice
        ) ||
        unitPrice < 0
      ) {
        return "Unit price must be 0 or greater.";
      }

      const displayOrder =
        Number(
          itemForm.display_order
        );

      if (
        !Number.isInteger(
          displayOrder
        ) ||
        displayOrder < 0
      ) {
        return "Display order must be a whole number that is 0 or greater.";
      }

      return "";
    };

  const buildItemPayload =
    () => ({
      service_id:
        itemForm.service_id
          ? Number(
              itemForm.service_id
            )
          : null,

      title:
        cleanText(
          itemForm.title
        ),

      description:
        optionalText(
          itemForm.description
        ),

      quantity:
        Number(
          itemForm.quantity
        ),

      unit_price:
        Number(
          itemForm.unit_price
        ),

      optional:
        Boolean(
          itemForm.optional
        ),

      approved:
        Boolean(
          itemForm.approved
        ),

      display_order:
        Number(
          itemForm.display_order ||
            0
        ),
    });

  const reloadProposalItems =
    async () => {
      if (!selectedProposal) {
        return;
      }

      const response =
        await Api.getDevelopmentProposalItems(
          selectedProposal.id
        );

      setProposalItems(
        response?.items || []
      );

      await loadData();

      const refreshed =
        await Api.getDevelopmentProposal(
          selectedProposal.id
        );

      if (
        refreshed?.proposal
      ) {
        setSelectedProposal(
          refreshed.proposal
        );
      }
    };

  const handleItemSubmit =
    async (event) => {
      event.preventDefault();
      clearMessages();

      const validationError =
        validateItemForm();

      if (validationError) {
        setError(
          validationError
        );

        return;
      }

      setIsSaving(true);

      try {
        const payload =
          buildItemPayload();

        if (selectedItem) {
          await Api.updateDevelopmentProposalItem(
            selectedItem.id,
            payload
          );

          setSuccess(
            "Proposal item updated."
          );
        } else {
          await Api.createDevelopmentProposalItem(
            selectedProposal.id,
            payload
          );

          setSuccess(
            "Proposal item created."
          );
        }

        await reloadProposalItems();

        setSelectedItem(null);

        setItemForm({
          ...EMPTY_ITEM_FORM,
        });

        setShowItemForm(false);
      } catch (requestError) {
        setError(
          requestError.message ||
            "The proposal item could not be saved."
        );
      } finally {
        setIsSaving(false);
      }
    };

  const deleteItem =
    async (item) => {
      const confirmed =
        window.confirm(
          `Delete proposal item "${item.title}"?`
        );

      if (!confirmed) {
        return;
      }

      clearMessages();

      try {
        await Api.deleteDevelopmentProposalItem(
          item.id
        );

        setSuccess(
          "Proposal item deleted."
        );

        await reloadProposalItems();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The proposal item could not be deleted."
        );
      }
    };

  return (
    <div className="development-proposals-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Proposal management
          </p>

          <h2>Proposals</h2>

          <p>
            Create itemized proposals,
            manage scope and terms, and
            track sent and accepted status.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={
              openNewProposalForm
            }
          >
            Add proposal
          </button>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {showProposalForm && (
        <ProposalForm
          formData={
            proposalForm
          }
          clients={clients}
          projects={
            availableProjects
          }
          selectedProposal={
            selectedProposal
          }
          isSaving={isSaving}
          onChange={
            handleProposalChange
          }
          onSubmit={
            handleProposalSubmit
          }
          onCancel={
            closeProposalForm
          }
        />
      )}

      {showItemManager &&
        selectedProposal && (
          <ProposalItemManager
            proposal={
              selectedProposal
            }
            items={
              proposalItems
            }
            services={services}
            itemForm={itemForm}
            selectedItem={
              selectedItem
            }
            showItemForm={
              showItemForm
            }
            isLoading={
              isLoadingItems
            }
            isSaving={isSaving}
            onClose={
              closeItemManager
            }
            onAddItem={
              openNewItemForm
            }
            onEditItem={
              openEditItemForm
            }
            onDeleteItem={
              deleteItem
            }
            onItemChange={
              handleItemChange
            }
            onItemSubmit={
              handleItemSubmit
            }
            onCancelItem={() => {
              setSelectedItem(
                null
              );

              setItemForm({
                ...EMPTY_ITEM_FORM,
              });

              setShowItemForm(
                false
              );
            }}
          />
        )}

      <section className="development-proposal-summary-grid">
        <SummaryCard
          label="All proposals"
          value={summary.total}
          active={
            statusFilter === "all"
          }
          onClick={() =>
            setStatusFilter("all")
          }
        />

        <SummaryCard
          label="Draft"
          value={summary.draft}
          active={
            statusFilter === "draft"
          }
          onClick={() =>
            setStatusFilter("draft")
          }
        />

        <SummaryCard
          label="Sent"
          value={summary.sent}
          active={
            statusFilter === "sent"
          }
          onClick={() =>
            setStatusFilter("sent")
          }
        />

        <SummaryCard
          label="Accepted"
          value={summary.accepted}
          active={
            statusFilter ===
            "accepted"
          }
          onClick={() =>
            setStatusFilter(
              "accepted"
            )
          }
        />

        <SummaryCard
          label="Total value"
          value={formatCurrency(
            summary.totalValue
          )}
          active={false}
          onClick={() =>
            setStatusFilter("all")
          }
        />
      </section>

      <section className="development-proposals-panel">
        <div className="development-proposals-toolbar">
          <div>
            <h3>
              Proposal records
            </h3>

            <p>
              {
                filteredProposals.length
              }{" "}
              proposal
              {filteredProposals.length ===
              1
                ? ""
                : "s"}{" "}
              shown
            </p>
          </div>

          <div className="development-proposal-filters">
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search proposals..."
              aria-label="Search proposals"
            />

            <select
              value={clientFilter}
              onChange={(event) =>
                setClientFilter(
                  event.target.value
                )
              }
            >
              <option value="">
                All clients
              </option>

              {clients.map(
                (client) => (
                  <option
                    key={client.id}
                    value={client.id}
                  >
                    {getClientName(
                      client
                    )}
                  </option>
                )
              )}
            </select>

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <option value="all">
                All statuses
              </option>

              {STATUS_OPTIONS.map(
                (status) => (
                  <option
                    key={status}
                    value={status}
                  >
                    {formatLabel(
                      status
                    )}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="development-admin-state">
            Loading proposals...
          </div>
        ) : filteredProposals.length ===
          0 ? (
          <div className="development-admin-empty">
            No proposals match the
            current filters.
          </div>
        ) : (
          <div className="development-proposal-card-grid">
            {filteredProposals.map(
              (proposal) => (
                <ProposalCard
                  key={proposal.id}
                  proposal={
                    proposal
                  }
                  client={clientMap.get(
                    Number(
                      proposal.client_id
                    )
                  )}
                  project={projectMap.get(
                    Number(
                      proposal.project_id
                    )
                  )}
                  onEdit={() =>
                    openEditProposalForm(
                      proposal
                    )
                  }
                  onManageItems={() =>
                    openItemManager(
                      proposal
                    )
                  }
                  onMarkSent={() =>
                    markProposalSent(
                      proposal
                    )
                  }
                  onMarkAccepted={() =>
                    markProposalAccepted(
                      proposal
                    )
                  }
                  onCancel={() =>
                    deactivateProposal(
                      proposal
                    )
                  }
                />
              )
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`development-proposal-summary-card${
        active ? " active" : ""
      }`}
      onClick={onClick}
    >
      <span>{label}</span>

      <strong>{value}</strong>
    </button>
  );
}

function ProposalCard({
  proposal,
  client,
  project,
  onEdit,
  onManageItems,
  onMarkSent,
  onMarkAccepted,
  onCancel,
}) {
  const sent =
    Number(proposal.sent) === 1;

  const accepted =
    Number(
      proposal.accepted
    ) === 1;

  return (
    <article className="development-proposal-card">
      <div className="development-proposal-card-header">
        <div>
          <p>
            {proposal.proposal_number}
          </p>

          <h4>
            {proposal.title}
          </h4>

          <span>
            {getClientName(client)}

            {project?.title
              ? ` — ${project.title}`
              : ""}
          </span>
        </div>

        <span
          className={`development-proposal-status ${proposal.status}`}
        >
          {formatLabel(
            proposal.status
          )}
        </span>
      </div>

      <div className="development-proposal-financial-grid">
        <div>
          <span>Subtotal</span>

          <strong>
            {formatCurrency(
              proposal.subtotal
            )}
          </strong>
        </div>

        <div>
          <span>Total</span>

          <strong>
            {formatCurrency(
              proposal.total_amount
            )}
          </strong>
        </div>

        <div>
          <span>Deposit</span>

          <strong>
            {proposal.deposit_percentage ||
              0}
            %
          </strong>
        </div>

        <div>
          <span>Expires</span>

          <strong>
            {formatDate(
              proposal.expiration_date
            )}
          </strong>
        </div>
      </div>

      <p className="development-proposal-summary">
        {proposal.scope_summary ||
          proposal.introduction ||
          "No proposal summary has been added."}
      </p>

      <div className="development-proposal-flags">
        <span
          className={
            sent ? "active" : ""
          }
        >
          {sent ? "✓" : "○"} Sent
        </span>

        <span
          className={
            accepted
              ? "active"
              : ""
          }
        >
          {accepted
            ? "✓"
            : "○"}{" "}
          Accepted
        </span>
      </div>

      <div className="development-proposal-card-actions">
        <button
          type="button"
          onClick={onManageItems}
        >
          Items
        </button>

        <button
          type="button"
          onClick={onEdit}
        >
          Edit
        </button>

        {!sent && (
          <button
            type="button"
            onClick={onMarkSent}
          >
            Mark sent
          </button>
        )}

        {!accepted && (
          <button
            type="button"
            className="accept"
            onClick={
              onMarkAccepted
            }
          >
            Accept
          </button>
        )}

        {proposal.status !==
          "cancelled" && (
          <button
            type="button"
            className="cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
      </div>
    </article>
  );
}

function ProposalForm({
  formData,
  clients,
  projects,
  selectedProposal,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-proposal-form-panel">
      <div className="development-proposal-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedProposal
              ? "Edit proposal"
              : "New proposal"}
          </p>

          <h3>
            {selectedProposal
              ? selectedProposal.title
              : "Create proposal"}
          </h3>
        </div>

        <button
          type="button"
          className="development-proposal-close-button"
          onClick={onCancel}
          aria-label="Close proposal form"
        >
          ×
        </button>
      </div>

      <form
        className="development-proposal-form"
        onSubmit={onSubmit}
      >
        <div className="development-proposal-form-grid">
          <SelectField
            label="Client"
            name="client_id"
            value={
              formData.client_id
            }
            onChange={onChange}
            required
          >
            <option value="">
              Select client
            </option>

            {clients.map(
              (client) => (
                <option
                  key={client.id}
                  value={client.id}
                >
                  {getClientName(
                    client
                  )}
                </option>
              )
            )}
          </SelectField>

          <SelectField
            label="Project"
            name="project_id"
            value={
              formData.project_id
            }
            onChange={onChange}
          >
            <option value="">
              No linked project
            </option>

            {projects.map(
              (project) => (
                <option
                  key={project.id}
                  value={project.id}
                >
                  {project.title}
                </option>
              )
            )}
          </SelectField>

          <FormField
            label="Title"
            name="title"
            value={formData.title}
            onChange={onChange}
            required
            fullWidth
          />

          <FormField
            label="Proposal number"
            name="proposal_number"
            value={
              formData.proposal_number
            }
            onChange={onChange}
            placeholder="Generated automatically when blank"
          />

          <SelectField
            label="Status"
            name="status"
            value={formData.status}
            onChange={onChange}
          >
            {STATUS_OPTIONS.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {formatLabel(
                    status
                  )}
                </option>
              )
            )}
          </SelectField>

          <FormField
            label="Estimated timeline"
            name="estimated_timeline"
            value={
              formData.estimated_timeline
            }
            onChange={onChange}
            placeholder="Four to six weeks"
          />

          <FormField
            label="Expiration date"
            name="expiration_date"
            type="date"
            value={
              formData.expiration_date
            }
            onChange={onChange}
          />

          <FormField
            label="Deposit percentage"
            name="deposit_percentage"
            type="number"
            value={
              formData.deposit_percentage
            }
            onChange={onChange}
            min="0"
            max="100"
            step="0.01"
          />

          <FormField
            label="Discount amount"
            name="discount_amount"
            type="number"
            value={
              formData.discount_amount
            }
            onChange={onChange}
            min="0"
            step="0.01"
          />

          <FormField
            label="Tax amount"
            name="tax_amount"
            type="number"
            value={
              formData.tax_amount
            }
            onChange={onChange}
            min="0"
            step="0.01"
          />

          <TextAreaField
            label="Introduction"
            name="introduction"
            value={
              formData.introduction
            }
            onChange={onChange}
            rows="4"
          />

          <TextAreaField
            label="Scope summary"
            name="scope_summary"
            value={
              formData.scope_summary
            }
            onChange={onChange}
            rows="5"
          />

          <TextAreaField
            label="Terms"
            name="terms"
            value={formData.terms}
            onChange={onChange}
            rows="5"
          />

          <TextAreaField
            label="Internal notes"
            name="notes"
            value={formData.notes}
            onChange={onChange}
            rows="4"
          />

          {formData.sent && (
            <FormField
              label="Sent date"
              name="sent_date"
              type="date"
              value={
                formData.sent_date
              }
              onChange={onChange}
            />
          )}

          {formData.accepted && (
            <FormField
              label="Accepted date"
              name="accepted_date"
              type="date"
              value={
                formData.accepted_date
              }
              onChange={onChange}
            />
          )}
        </div>

        <div className="development-proposal-checkbox-row">
          <CheckboxField
            name="sent"
            label="Proposal sent"
            checked={formData.sent}
            onChange={onChange}
          />

          <CheckboxField
            name="accepted"
            label="Proposal accepted"
            checked={
              formData.accepted
            }
            onChange={onChange}
          />
        </div>

        <div className="development-proposal-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedProposal
                ? "Save changes"
                : "Create proposal"}
          </button>
        </div>
      </form>
    </section>
  );
}

function ProposalItemManager({
  proposal,
  items,
  services,
  itemForm,
  selectedItem,
  showItemForm,
  isLoading,
  isSaving,
  onClose,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onItemChange,
  onItemSubmit,
  onCancelItem,
}) {
  const subtotal =
    items.reduce(
      (total, item) =>
        total +
        (
          Number(
            item.line_total
          ) || 0
        ),
      0
    );

  return (
    <section className="development-proposal-items-panel">
      <div className="development-proposal-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            Proposal items
          </p>

          <h3>
            {proposal.title}
          </h3>
        </div>

        <div className="development-proposal-item-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={onAddItem}
          >
            Add item
          </button>

          <button
            type="button"
            className="development-proposal-close-button"
            onClick={onClose}
            aria-label="Close proposal items"
          >
            ×
          </button>
        </div>
      </div>

      {showItemForm && (
        <form
          className="development-proposal-item-form"
          onSubmit={
            onItemSubmit
          }
        >
          <div className="development-proposal-form-grid">
            <SelectField
              label="Service"
              name="service_id"
              value={
                itemForm.service_id
              }
              onChange={
                onItemChange
              }
            >
              <option value="">
                Custom item
              </option>

              {services.map(
                (service) => (
                  <option
                    key={service.id}
                    value={service.id}
                  >
                    {service.name}
                  </option>
                )
              )}
            </SelectField>

            <FormField
              label="Title"
              name="title"
              value={
                itemForm.title
              }
              onChange={
                onItemChange
              }
              required
            />

            <FormField
              label="Quantity"
              name="quantity"
              type="number"
              value={
                itemForm.quantity
              }
              onChange={
                onItemChange
              }
              min="0.01"
              step="0.01"
            />

            <FormField
              label="Unit price"
              name="unit_price"
              type="number"
              value={
                itemForm.unit_price
              }
              onChange={
                onItemChange
              }
              min="0"
              step="0.01"
            />

            <FormField
              label="Display order"
              name="display_order"
              type="number"
              value={
                itemForm.display_order
              }
              onChange={
                onItemChange
              }
              min="0"
              step="1"
            />

            <TextAreaField
              label="Description"
              name="description"
              value={
                itemForm.description
              }
              onChange={
                onItemChange
              }
              rows="4"
            />
          </div>

          <div className="development-proposal-checkbox-row">
            <CheckboxField
              name="optional"
              label="Optional item"
              checked={
                itemForm.optional
              }
              onChange={
                onItemChange
              }
            />

            <CheckboxField
              name="approved"
              label="Approved"
              checked={
                itemForm.approved
              }
              onChange={
                onItemChange
              }
            />
          </div>

          <div className="development-proposal-form-actions">
            <button
              type="button"
              className="development-admin-button secondary"
              onClick={
                onCancelItem
              }
              disabled={isSaving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="development-admin-button primary"
              disabled={isSaving}
            >
              {isSaving
                ? "Saving..."
                : selectedItem
                  ? "Save item"
                  : "Create item"}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="development-admin-state">
          Loading proposal items...
        </div>
      ) : items.length === 0 ? (
        <div className="development-admin-empty">
          No items have been added to
          this proposal.
        </div>
      ) : (
        <div className="development-proposal-item-table-wrapper">
          <table className="development-proposal-item-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Unit price</th>
                <th>Total</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {items.map(
                (item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>
                        {item.title}
                      </strong>

                      <span>
                        {item.description ||
                          "No description"}
                      </span>
                    </td>

                    <td>
                      {item.quantity}
                    </td>

                    <td>
                      {formatCurrency(
                        item.unit_price
                      )}
                    </td>

                    <td>
                      {formatCurrency(
                        item.line_total
                      )}
                    </td>

                    <td>
                      {Number(
                        item.optional
                      ) === 1
                        ? "Optional"
                        : "Included"}

                      {Number(
                        item.approved
                      ) === 1
                        ? " / Approved"
                        : " / Not approved"}
                    </td>

                    <td>
                      <div className="development-proposal-item-actions">
                        <button
                          type="button"
                          onClick={() =>
                            onEditItem(
                              item
                            )
                          }
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className="danger"
                          onClick={() =>
                            onDeleteItem(
                              item
                            )
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan="3">
                  Proposal subtotal
                </td>

                <td>
                  {formatCurrency(
                    subtotal
                  )}
                </td>

                <td colSpan="2" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

function FormField({
  label,
  name,
  type = "text",
  value,
  onChange,
  required = false,
  placeholder = "",
  min,
  max,
  step,
  fullWidth = false,
}) {
  return (
    <label
      className={`development-proposal-field${
        fullWidth
          ? " full-width"
          : ""
      }`}
    >
      <span>
        {label}

        {required && " *"}
      </span>

      <input
        type={type}
        name={name}
        value={safeString(value)}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
      />
    </label>
  );
}

function SelectField({
  label,
  name,
  value,
  onChange,
  required = false,
  children,
}) {
  return (
    <label className="development-proposal-field">
      <span>
        {label}

        {required && " *"}
      </span>

      <select
        name={name}
        value={safeString(value)}
        onChange={onChange}
        required={required}
      >
        {children}
      </select>
    </label>
  );
}

function TextAreaField({
  label,
  name,
  value,
  onChange,
  rows = "4",
}) {
  return (
    <label className="development-proposal-field full-width">
      <span>{label}</span>

      <textarea
        name={name}
        value={safeString(value)}
        onChange={onChange}
        rows={rows}
      />
    </label>
  );
}

function CheckboxField({
  name,
  label,
  checked,
  onChange,
}) {
  return (
    <label>
      <input
        type="checkbox"
        name={name}
        checked={Boolean(checked)}
        onChange={onChange}
      />

      <span>{label}</span>
    </label>
  );
}

export default DevelopmentProposals;